import React, { useState } from "react";
import styled from "styled-components";
import { signIn } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { storeAccessToken, validateAndRefreshTokens, handleAuthError } from '../utils/authUtils';
import { Eye, EyeOff } from 'lucide-react';

const Page = styled.div`
  min-height: 100vh;
  background-color: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  padding: 2.5rem 2rem;
  max-width: 420px;
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
`;

const Heading = styled.h1`
  font-size: 1.8rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 1px #6366f1;
  }
`;

// Inherit padding-right for icon spacing
const PasswordInput = styled(Input)`
  padding-right: 2.5rem;
  margin-bottom: 0;
`;

const PasswordContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const ToggleIcon = styled.span`
  position: absolute;
  top: 50%;
  right: 0.9rem;
  transform: translateY(-50%);
  cursor: pointer;
  margin-bottom: 0;
  color: #666;
`;

const Button = styled.button`
  width: 100%;
  background-color: #002e5d;
  color: white;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background-color: #001a3d;
  }
`;

const ForgotPassword = styled.p`
  font-size: 0.9rem;
  color: #666;
  text-align: right;
  margin-top: -0.5rem;
  margin-bottom: 1rem;

  a {
    color: #6366f1;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const FooterText = styled.p`
  font-size: 0.9rem;
  color: #666;
  text-align: center;
  margin-top: 1.5rem;

  a {
    color: #6366f1;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const StatusMessage = styled.p`
  margin-top: 1rem;
  color: ${(props) => (props.success ? "green" : "red")} ;
  font-size: 0.9rem;
  text-align: center;
`;

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      // First, try to validate existing tokens
      const hasValidTokens = await validateAndRefreshTokens();
      if (hasValidTokens) {
        setStatus("✅ Login successful");
        navigate("/dashboard/profile");
        return;
      }

      // If no valid tokens, proceed with fresh login
      const result = await signIn({ username: email, password });
      
      await storeAccessToken();
      setStatus("✅ Login successful");
      navigate("/dashboard/profile");
    } catch (error) {
      console.error(error);
      await handleAuthError();
      setStatus(`❌ ${error.message}`);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <Page>
      <Card>
        <Heading>Log in to Skroop</Heading>

        <form onSubmit={handleLogin}>
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <PasswordContainer>
            <PasswordInput
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <ToggleIcon onClick={toggleShowPassword}>
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </ToggleIcon>
          </PasswordContainer>

          <ForgotPassword>
            <a href="/forgot" onClick={() => console.log('Forgot Password clicked')}>
              Forgot Password?
            </a>
          </ForgotPassword>

          <Button type="submit">Log In</Button>
        </form>

        {status && (
          <StatusMessage success={status.startsWith("✅")}>{status}</StatusMessage>
        )}

        <FooterText>
          Don’t have an account? <a href="/signup">Sign up</a>
        </FooterText>
      </Card>
    </Page>
  );
}
